import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  deleteUser,
  getAllUsersTable,
  getUserDetails,
  createUser,
  updateUser,
  type UserResponse,
  CreateUserPayload,
  UpdateUserPayload,
  getUsersPart,
  UserPartDataResponse,
} from '@app/api/users.api'
import { readUser } from '@app/store/services/local-storage.service'

import { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

import { deleteFirmData, updateFirmData } from './firms-slice'
import { updateProfileData, getProfileData } from './profile-slice'
import { deleteDeviceData } from './device-slice'

type UsersState = {
  allUsersStatus: Status
  usersPartStatus: Status
  userDetailsStatus: Status
  createUserStatus: Status
  updateUserStatus: Status
  deleteUserStatus: Status
  allUsersData: Record<string, UserResponse>
  usersPartData: Record<string, UserPartDataResponse>
}

const initialState: UsersState = {
  allUsersData: {},
  usersPartData: {},
  allUsersStatus: 'idle',
  usersPartStatus: 'idle',
  userDetailsStatus: 'idle',
  createUserStatus: 'idle',
  updateUserStatus: 'idle',
  deleteUserStatus: 'idle',
}

export const getAllUsersTableData = createAsyncThunk<UserResponse[]>(
  'users/allTableData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllUsersTable()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getUserDetailsData = createAsyncThunk<UserResponse, string>(
  'users/details',
  async (id, { rejectWithValue }) => {
    try {
      const res = await getUserDetails(id)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getUsersPartData = createAsyncThunk<UserPartDataResponse[]>(
  'users/partData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getUsersPart()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createUserData = createAsyncThunk<string, CreateUserPayload>(
  'user/createUser',
  async (createUserPayload, { rejectWithValue }) => {
    try {
      const res = await createUser(createUserPayload)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateUserData = createAsyncThunk<void, UpdateUserPayload>(
  'user/update',
  async (updateUserPayload, { rejectWithValue, dispatch }) => {
    try {
      await updateUser(updateUserPayload)

      const currentUserId = readUser()
      if (currentUserId && currentUserId === updateUserPayload.id) {
        dispatch(getProfileData(currentUserId))
      }
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const deleteUserData = createAsyncThunk<void, string>('user/deleteUser', async (id, { rejectWithValue }) => {
  try {
    await deleteUser(id)
    return
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateUserData.pending, (state) => {
        state.updateUserStatus = 'loading'
      })
      .addCase(getUserDetailsData.pending, (state) => {
        state.userDetailsStatus = 'loading'
      })
      .addCase(createUserData.pending, (state) => {
        state.createUserStatus = 'loading'
      })
      .addCase(getAllUsersTableData.pending, (state) => {
        state.allUsersStatus = 'loading'
      })
      .addCase(getUsersPartData.pending, (state) => {
        state.usersPartStatus = 'loading'
      })
      .addCase(deleteUserData.pending, (state) => {
        state.deleteUserStatus = 'loading'
      })
      .addCase(updateUserData.rejected, (state) => {
        state.updateUserStatus = 'failed'
      })
      .addCase(createUserData.rejected, (state) => {
        state.createUserStatus = 'failed'
      })
      .addCase(getUserDetailsData.rejected, (state) => {
        state.userDetailsStatus = 'failed'
      })
      .addCase(getUsersPartData.rejected, (state) => {
        state.usersPartStatus = 'failed'
      })
      .addCase(getAllUsersTableData.rejected, (state) => {
        state.allUsersStatus = 'failed'
      })
      .addCase(deleteUserData.rejected, (state) => {
        state.deleteUserStatus = 'failed'
      })
      .addCase(getAllUsersTableData.fulfilled, (state, action) => {
        action.payload.forEach((user) => {
          const id = user.id
          const existing = state.allUsersData[id] ?? {}

          state.allUsersData[id] = {
            ...existing,
            ...user,
          }
        })

        state.allUsersStatus = 'succeeded'
      })
      .addCase(updateUserData.fulfilled, (state, action) => {
        const { id, data } = action.meta.arg

        state.allUsersData[id] = { id, ...data }
        state.usersPartData[id] = { id, firms: data.firms, devices: data.devices }

        state.updateUserStatus = 'succeeded'
      })
      .addCase(createUserData.fulfilled, (state, action) => {
        const { password, ...requestData } = action.meta.arg

        const newUser = {
          id: action.payload,
          ...requestData,
        }

        state.allUsersData = {
          [newUser.id]: newUser,
          ...state.allUsersData,
        }
        state.usersPartData[newUser.id] = {
          id: newUser.id,
          firms: newUser.firms,
          devices: newUser.devices,
        }

        state.createUserStatus = 'succeeded'
      })
      .addCase(getUserDetailsData.fulfilled, (state, action) => {
        const { id, ...userDetailsData } = action.payload
        const existingUser = state.allUsersData[id]

        state.allUsersData[id] = {
          ...existingUser,
          ...userDetailsData,
        }
        if (userDetailsData.firms && userDetailsData.devices) {
          state.usersPartData[id] = {
            id,
            firms: userDetailsData.firms,
            devices: userDetailsData.devices,
          }
        }

        state.userDetailsStatus = 'succeeded'
      })
      .addCase(getUsersPartData.fulfilled, (state, action) => {
        action.payload.forEach((user) => {
          state.usersPartData[user.id] = user
        })

        state.usersPartStatus = 'succeeded'
      })
      .addCase(deleteUserData.fulfilled, (state, action) => {
        const deletedUserId = action.meta.arg

        if (!deletedUserId) return

        delete state.allUsersData[deletedUserId]
        delete state.usersPartData[deletedUserId]

        state.deleteUserStatus = 'succeeded'
      })
      .addCase(updateFirmData.fulfilled, (state, action) => {
        const { data } = action.meta.arg

        if (data?.usersToUpdate?.length) {
          data.usersToUpdate.forEach(({ id, devices }) => {
            const userPart = state.usersPartData[id]

            if (userPart) {
              userPart.devices = userPart.devices.filter((deviceId) => !devices.includes(deviceId))
            }

            const existingUser = state.allUsersData[id]
            if (existingUser) {
              const filteredDevices = existingUser.devices?.filter((deviceId) => !devices.includes(deviceId))

              state.allUsersData[id] = {
                ...existingUser,
                devices: filteredDevices,
              }
            }
          })
        }
      })
      .addCase(deleteFirmData.fulfilled, (state, action) => {
        const id = action.meta.arg

        Object.values(state.usersPartData).forEach((userData) => {
          userData.firms = userData.firms.filter((firmId) => firmId !== id)
        })

        Object.entries(state.allUsersData).forEach(([userId, userData]) => {
          if (!userData?.firms?.length) return

          if (userData.firms.includes(id)) {
            const updatedFirms = userData.firms.filter((firmId) => firmId !== id)

            state.allUsersData[userId] = {
              ...userData,
              firms: updatedFirms,
            }
          }
        })
      })
      .addCase(updateProfileData.fulfilled, (state, action) => {
        const { id, data } = action.meta.arg

        if (state.allUsersData[id]) {
          const existingUser = state.allUsersData[id]

          state.allUsersData[id] = {
            ...existingUser,
            ...data,
          }
        }
      })
      .addCase(deleteDeviceData.fulfilled, (state, action) => {
        const id = action.meta.arg

        Object.values(state.usersPartData).forEach((userData) => {
          userData.devices = userData.devices.filter((deviceId) => deviceId !== id)
        })

        const allUsersData = state.allUsersData

        Object.values(allUsersData).forEach((user) => {
          const { devices } = user

          if (!devices?.length) return
          if (devices.includes(id)) {
            const updatedDevices = devices.filter((deviceId) => deviceId !== id)

            state.allUsersData[user.id] = {
              ...user,
              devices: updatedDevices,
            }
          }
        })
      })
  },
})

export default userSlice.reducer
